import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import iso3166 from "iso-3166-2";

import { FirebaseAuth } from "../../FirebaseAuth/firebase";
import Loader from "../../Loader";

const titleCaseLocationPart = (value = "") => String(value || "")
  .trim()
  .split(/\s+/)
  .map((part) => (part ? `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}` : ""))
  .join(" ");

const formatLocation = (location = "") => {
  const parts = String(location || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return "";

  const country = parts[parts.length - 1].toUpperCase();
  const city = titleCaseLocationPart(parts[0]);
  const regionCode = parts.length > 2 ? parts[1].toUpperCase() : "";
  const subdivision = regionCode ? iso3166.subdivision(country, regionCode) : null;
  const region = regionCode
    ? subdivision?.name || titleCaseLocationPart(regionCode)
    : titleCaseLocationPart(parts[1] || "");

  return [city, region, country].filter(Boolean).join(", ");
};

const actionColor = (action) => {
  switch ((action || "").toLowerCase()) {
    case "delete":
      return "error.main";
    case "signin":
    case "signup":
    case "logout":
    case "signed in":
    case "signed out":
    case "signed_in":
    case "signed_out":
      return "secondary.main";
    case "create":
    case "updated":
    case "password updated":
    case "user name updated":
    case "email address updated":
      return "success.main";
    default:
      return "text.primary";
  }
};

const formatTimestamp = (time) => {
  if (!time) return { date: "-", clock: "" };

  const dt = typeof time.toDate === "function" ? time.toDate() : new Date(time);
  if (Number.isNaN(dt.getTime())) return { date: "-", clock: "" };

  return {
    date: dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    }),
    clock: dt.toLocaleTimeString("en-GB", {
      hour12: false,
      timeZone: "UTC",
    }),
  };
};

const AuditLogs = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    let mounted = true;

    const fetchLogs = async () => {
      const user = FirebaseAuth.auth().currentUser;
      if (!user) {
        if (mounted) {
          setLogs([]);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const snapshot = await FirebaseAuth
          .firestore()
          .collection("users")
          .doc(user.uid)
          .collection("activities")
          .orderBy("created_at", "desc")
          .get();

        const rows = snapshot.docs.map((doc) => {
          const data = doc.data() || {};
          const metadata = data.metadata || {};
          const userAgent = data.user_agent || metadata.user_agent || "";
          return {
            id: doc.id,
            action: data.action || "",
            time: data.created_at || null,
            description: data.description || metadata.description || userAgent || "",
            entity: data.entity || "",
            entityId: data.entity_id || "",
            location: data.location || metadata.location || "",
            ip: data.ip || data.ip_address || metadata.ip || metadata.ip_address || "",
            userAgent,
          };
        });

        if (mounted) setLogs(rows);
      } catch (_error) {
        if (mounted) setLogs([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchLogs();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredLogs = useMemo(() => {
    if (!search.trim()) return logs;

    const q = search.trim().toLowerCase();
    return logs.filter((entry) => {
      const formatted = formatTimestamp(entry.time);
      const formattedLocation = formatLocation(entry.location);
      const searchable = [
        entry.action || "",
        entry.description || "",
        entry.entity || "",
        entry.entityId || "",
        entry.location || "",
        formattedLocation || "",
        entry.ip || "",
        entry.userAgent || "",
        formatted.date,
        formatted.clock,
      ].join(" ").toLowerCase();
      return searchable.includes(q);
    });
  }, [logs, search]);

  const pagedLogs = useMemo(() => {
    const start = page * rowsPerPage;
    return {
      total: filteredLogs.length,
      rows: filteredLogs.slice(start, start + rowsPerPage),
    };
  }, [filteredLogs, page, rowsPerPage]);

  return (
    <Box sx={{ textAlign: "left" }}>
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                Audit Logs
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Review recent account activity and sign-in events.
              </Typography>
            </Box>
            <TextField
              size="small"
              placeholder="Search audit logs"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(0);
              }}
              sx={{ width: { xs: "100%", sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>
        <Divider />

        {loading ? (
          <Loader variant="section" label="Loading audit logs..." />
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: theme.palette.mode === "dark" ? theme.palette.action.hover : theme.palette.grey[100],
                    }}
                  >
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedLogs.rows.map((entry) => {
                    const formatted = formatTimestamp(entry.time);
                    const formattedLocation = formatLocation(entry.location);
                    return (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography variant="body2">{formatted.date}</Typography>
                            <Typography variant="caption">{formatted.clock}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: actionColor(entry.action), fontWeight: 500 }}>
                            {(entry.action || "").toUpperCase()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography variant="body2">{formattedLocation || "-"}</Typography>
                            <Typography variant="caption">{entry.ip || "-"}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entry.description || "-"}</Typography>
                          {entry.userAgent && (
                            <Typography variant="caption" color="text.secondary" display="block" noWrap title={entry.userAgent}>
                              {entry.userAgent}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {pagedLogs.rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography variant="body2">No audit logs found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" alignItems="center" justifyContent="flex-end" px={2}>
              <TablePagination
                component="div"
                count={pagedLogs.total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 20, 50]}
                labelRowsPerPage="Show"
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AuditLogs;
